import React, { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { api as afriApi, getToken as getAfriToken } from "@/lib/api";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, Wallet, TrendingUp, Info, ArrowRightLeft, Coins, Gift, Sparkles, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { isPlyxStandaloneMode } from "@/lib/plyxStandalone";

const PlyxWallet = () => {
  const { user, loading } = usePlyxAuth();
  const [data, setData] = useState(null);
  const [coins, setCoins] = useState(null);
  const [packs, setPacks] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [linking, setLinking] = useState(false);
  const [buying, setBuying] = useState(null); // pack_code while creating checkout
  const [searchParams, setSearchParams] = useSearchParams();

  const reload = () => {
    setLoadingData(true);
    Promise.all([
      plyxApi.get("/plyx/me/credits"),
      plyxApi.get("/plyx/coins/me").catch(() => ({ data: null })),
      plyxApi.get("/plyx/coins/packs").catch(() => ({ data: { packs: [] } })),
    ])
      .then(([r, c, p]) => {
        setData(r.data);
        setCoins(c.data);
        setPacks(p.data?.packs || []);
      })
      .catch(() => toast.error("Could not load wallet"))
      .finally(() => setLoadingData(false));
  };

  useEffect(() => {
    if (!user) return;
    reload();
  }, [user]);

  // Poll the payment-status endpoint once the buyer returns from Stripe with
  // ?coins_session_id=. This triggers the webhook fallback path so coins are
  // credited even if the Stripe webhook is delayed/blocked.
  useEffect(() => {
    const sid = searchParams.get("coins_session_id");
    const cancelled = searchParams.get("coins_cancelled");
    if (cancelled) {
      toast.info("Coin purchase cancelled.");
      searchParams.delete("coins_cancelled");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    if (!sid) return;
    let tries = 0;
    let stopped = false;
    const poll = async () => {
      try {
        const r = await afriApi.get(`/payments/status/${sid}`);
        if (r.data?.payment_status === "paid") {
          toast.success(`+${r.data.coins || ""} coins added to your wallet!`, { duration: 5000 });
          searchParams.delete("coins_session_id");
          setSearchParams(searchParams, { replace: true });
          reload();
          return;
        }
      } catch { /* ignore */ }
      tries += 1;
      if (tries < 10 && !stopped) setTimeout(poll, 1500);
    };
    poll();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("coins_session_id")]);

  const buyPack = async (pack) => {
    setBuying(pack.code);
    try {
      const { data: res } = await plyxApi.post("/plyx/coins/checkout", {
        pack_code: pack.code,
        origin_url: window.location.origin,
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setBuying(null);
    }
  };

  const redeem = async () => {
    if (!data || data.balance <= 0) return;
    setRedeeming(true);
    try {
      const { data: res } = await plyxApi.post("/plyx/wallet/redeem");
      toast.success(`Redeemed £${res.redeemed.toFixed(2)} to your AfriMarket account`);
      reload();
    } catch (err) {
      const msg = err.response?.data?.detail || "Redeem failed";
      if (msg.includes("Link your AfriMarket")) {
        toast.error("Link your AfriMarket account first — see button below");
      } else {
        toast.error(msg);
      }
    } finally {
      setRedeeming(false);
    }
  };

  // Two-factor link: requires an active AfriMarket session in the same
  // browser. We send the AfriMarket token in the body (browsers can't forge
  // Cookie headers from JS).
  const link = async () => {
    const afriToken = getAfriToken();
    if (!afriToken) {
      toast.error("Please log into AfriMarket first (open it in another tab)");
      return;
    }
    setLinking(true);
    try {
      await plyxApi.post("/plyx/auth/link-afrimarket", { afri_token: afriToken });
      toast.success("AfriMarket account linked");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not link accounts");
    } finally {
      setLinking(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  }
  if (!user) return <Navigate to="/plyx/login" replace state={{ from: "/plyx/wallet" }}/>;
  if (loadingData) return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  if (!data) return null;

  const pctOfCap = data.daily_cap > 0 ? Math.min(100, (data.earned_today / data.daily_cap) * 100) : 0;
  const earnedMonth = typeof data.earned_month === "number" ? data.earned_month : 0;
  const monthlyCap = typeof data.monthly_cap === "number" ? data.monthly_cap : 0;
  const pctOfMonth = monthlyCap > 0 ? Math.min(100, (earnedMonth / monthlyCap) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-wallet-page">
      <div className="container-narrow py-10 max-w-2xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-[10px] uppercase tracking-[0.25em] text-white/70 mb-4">
          <Wallet className="w-3 h-3"/> Watch-to-earn · Plyx wallet
        </div>
        <h1 className="font-display text-5xl tracking-tight mb-2">£{data.balance.toFixed(2)}</h1>
        <p className="text-white/60 mb-6">
          {isPlyxStandaloneMode()
            ? "Your total Plyx credit balance. Cash out via bank transfer (coming soon)."
            : "Your total Plyx credit balance. Redeem it as AfriMarket credit you can spend at checkout."}
        </p>

        {/* Coins panel — sit between credit balance and redeem block */}
        {coins && (
          <div className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-[#FFB42D]/15 to-[#FF2D7A]/10 border border-white/10"
               data-testid="plyx-wallet-coins-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Coins className="w-6 h-6 text-[#FFB42D]" />
                <div>
                  <div className="text-2xl font-display tabular-nums" data-testid="plyx-wallet-coin-balance">
                    {coins.balance.toLocaleString()}
                  </div>
                  <div className="text-xs text-white/60 mt-0.5">
                    coins · worth £{coins.balance_gbp?.toFixed(2)} at face value
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-white/70">
                <div className="flex items-center gap-1" title="Lifetime gifts sent">
                  <Gift className="w-3 h-3 text-[#FF2D7A]" />
                  Sent: <strong className="text-white">{coins.gifts_sent_lifetime}</strong>
                </div>
                <div className="flex items-center gap-1" title="Lifetime gifts received">
                  <Sparkles className="w-3 h-3 text-white/85" />
                  Received: <strong className="text-white">{coins.gifts_received_lifetime}</strong>
                </div>
              </div>
            </div>
            <div className="text-[11px] text-white/50 mt-3">
              New accounts get 100 free coins. Top up below to gift creators more love.
            </div>
          </div>
        )}

        {/* Real-money coin packs */}
        {packs.length > 0 && (
          <div className="mb-6" data-testid="plyx-coin-packs">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-display text-2xl flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-[#FF2D7A]"/> Buy coins
              </h2>
              <div className="text-[11px] text-white/40">Secure checkout via Stripe</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {packs.map((p) => (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => buyPack(p)}
                  disabled={buying !== null}
                  data-testid={`plyx-coin-pack-${p.code}`}
                  className={`relative p-4 rounded-2xl border text-left transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100
                              ${p.popular
                                ? "border-[#FFB42D] bg-gradient-to-br from-[#FFB42D]/10 to-[#FF2D7A]/10"
                                : "border-white/15 bg-white/5 hover:border-white/30"}`}
                >
                  {p.popular && (
                    <div className="absolute -top-2 left-3 px-2 py-0.5 rounded-full bg-[#FFB42D] text-[10px] font-bold text-white uppercase tracking-wider">
                      Most popular
                    </div>
                  )}
                  <div className="text-xs text-white/60 uppercase tracking-wider mb-1">{p.label}</div>
                  <div className="flex items-center gap-1.5">
                    <Coins className="w-4 h-4 text-[#FFB42D]"/>
                    <span className="text-2xl font-display tabular-nums">{p.coins.toLocaleString()}</span>
                  </div>
                  {p.bonus_pct > 0 && (
                    <div className="text-[10px] text-white/85 font-semibold mt-0.5">+{p.bonus_pct}% bonus</div>
                  )}
                  <div className="mt-3 text-sm font-semibold">£{p.price_gbp.toFixed(2)}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-white/70">
                    {buying === p.code
                      ? <><Loader2 className="w-3 h-3 animate-spin"/> Redirecting…</>
                      : <>Buy now →</>}
                  </div>
                </button>
              ))}
            </div>
            <div className="text-[11px] text-white/40 mt-2">
              Coins land in your wallet within a few seconds of payment. All purchases are non-refundable.
            </div>
          </div>
        )}

        {/* Redeem to AfriMarket — hidden in standalone Plyx preview */}
        {!isPlyxStandaloneMode() && (
        <div className="p-5 rounded-2xl bg-gradient-to-br from-[#FF2D7A]/15 to-[#00E0C7]/15 border border-white/15 mb-6" data-testid="plyx-wallet-redeem-block">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <ArrowRightLeft className="w-5 h-5 text-[#FFB42D]"/>
              <div>
                <div className="font-semibold">Move to AfriMarket</div>
                <div className="text-xs text-white/60">
                  {user.linked_afrimarket_user_id
                    ? "Linked. Redeem your balance to spend on listings."
                    : "Link your AfriMarket account first."}
                </div>
              </div>
            </div>
            {user.linked_afrimarket_user_id ? (
              <button
                type="button"
                onClick={redeem}
                disabled={redeeming || data.balance <= 0}
                data-testid="plyx-wallet-redeem-btn"
                className="px-5 py-2 rounded-full font-semibold text-white text-sm disabled:opacity-40 inline-flex items-center gap-2"
                style={{ background: "#E85D4E" }}
              >
                {redeeming ? <Loader2 className="w-4 h-4 animate-spin"/> : <ArrowRightLeft className="w-4 h-4"/>}
                Redeem £{data.balance.toFixed(2)}
              </button>
            ) : (
              <button
                type="button"
                onClick={link}
                disabled={linking}
                data-testid="plyx-wallet-link-btn"
                className="px-5 py-2 rounded-full font-semibold text-white text-sm disabled:opacity-40 inline-flex items-center gap-2"
                style={{ background: "#E85D4E" }}
              >
                {linking ? <Loader2 className="w-4 h-4 animate-spin"/> : null}
                Link AfriMarket
              </button>
            )}
          </div>
        </div>
        )}

        {/* Today's progress */}
        <div className="p-5 rounded-2xl bg-white/5 border border-white/10 mb-6" data-testid="plyx-wallet-today">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#FFB42D]"/>
              <span className="font-semibold">Today</span>
            </div>
            <div className="text-sm">
              <strong>£{data.earned_today.toFixed(2)}</strong>
              <span className="text-white/50"> / £{data.daily_cap.toFixed(2)} cap</span>
            </div>
          </div>
          <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${pctOfCap}%`, background: "linear-gradient(90deg, #FF2D7A, #FFB42D, #00E0C7)" }}
            />
          </div>
          <div className="mt-3 text-xs text-white/50 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
            <span>Earn £{data.earn_per_view.toFixed(2)} for each video you watch ≥ {data.min_watch_sec}s — once per video, up to £{data.daily_cap.toFixed(2)}/day{monthlyCap > 0 ? ` and £${monthlyCap.toFixed(2)}/month` : ""}. Self-views don&apos;t count.</span>
          </div>
        </div>

        {/* This month's progress */}
        {monthlyCap > 0 && (
          <div className="p-5 rounded-2xl bg-white/5 border border-white/10 mb-6" data-testid="plyx-wallet-month">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-white/85"/>
                <span className="font-semibold">This month</span>
              </div>
              <div className="text-sm">
                <strong data-testid="plyx-wallet-earned-month">£{earnedMonth.toFixed(2)}</strong>
                <span className="text-white/50" data-testid="plyx-wallet-monthly-cap"> / £{monthlyCap.toFixed(2)} cap</span>
              </div>
            </div>
            <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full transition-all"
                style={{ width: `${pctOfMonth}%`, background: "linear-gradient(90deg, #00E0C7, #FFB42D, #FF2D7A)" }}
              />
            </div>
          </div>
        )}

        {/* Ledger */}
        <h2 className="font-display text-2xl mb-3">Recent earnings</h2>
        <div className="space-y-2">
          {data.recent.length === 0 && (
            <div className="p-6 rounded-xl bg-white/5 text-center text-white/50 text-sm">
              No earnings yet. <Link to="/plyx/feed" className="underline hover:text-white">Watch some videos</Link> to start earning.
            </div>
          )}
          {data.recent.map((r) => (
            <div key={r.credit_id} className="p-4 rounded-xl bg-white/5 border border-white/10 flex items-center justify-between text-sm" data-testid={`plyx-wallet-row-${r.credit_id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-[#FFB42D]/15 flex items-center justify-center shrink-0">
                  <Wallet className="w-4 h-4 text-[#FFB42D]"/>
                </div>
                <div className="min-w-0">
                  <div className="font-medium">Watched <Link to={`/plyx/v/${r.video_id}`} className="underline hover:text-white">video</Link></div>
                  <div className="text-xs text-white/40">{r.created_at?.slice(0, 16).replace("T", " ")} · {r.watched_sec.toFixed(0)}s watched</div>
                </div>
              </div>
              <div className="text-white/85 font-semibold">+£{r.amount.toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-xs text-white/40">
          {isPlyxStandaloneMode()
            ? "Plyx ledger tracks every video you watch — earnings cash out via your linked bank account."
            : <>Plyx ledger rows show as <strong>redeemed</strong> once moved to AfriMarket — no double-spending.</>}
        </div>
      </div>
    </div>
  );
};

export default PlyxWallet;
